import { SpeakerMap, Transcript } from "./ConsultTypes";
import { ConsultChatMessage } from "./ConsultChatTypes";
import { Suggestion } from "../../utils/healthcare/SugestionUtils";
import { Role } from "../admin/RoleTypes";
import { TeamMember } from "../admin/TeamTypes";
import { PractitionerWorkflowAssignment, WorkflowAssignment, WorkflowTemplate } from "./ConsultWorkflowTypes";
import type { UserUuid } from "../identity";


export interface AppointmentPractitioner {
	id: number;
	appointment_id: number;
	practitioner_id: UserUuid;
	role: 'primary' | 'secondary';
	timing_position: 'before' | 'during' | 'after';
	timing_minutes: number;
	created_at?: string;
	updated_at?: string;
}

export interface Appointment {
	id: number;
	patient_id: UserUuid;
	practitioner_id: UserUuid;
	appointment_type_id?: number;
	start_time?: Date;
	end_time?: Date;
	status: string;
	created_at: Date;
	updated_at: Date;
	location: string;
	room_number: string;
	category: string;
	title: string;
	consultation_link?: string;
	booking_timezone?: string;
	cliniko_id?: number | null;
	cliniko_data?: any;
	cliniko_synced_at?: string | null;
	created_in_cliniko?: boolean;
	cliniko_booking_link?: string | null;
	appointment_type?: AppointmentType;
	practitioners?: AppointmentPractitioner[];
}

/**
 * Create/update body. `send_confirmation` is NOT an appointment column — the API module lifts it
 * to the top level, beside `appointmentData`. `date` is REQUEST-ONLY: healthcare dropped the
 * column (86d42y068 S14) and no longer returns it, so reading one off a response is a bug.
 */
export type AppointmentSaveRequest = Partial<Appointment> & {
	date?: Date;
	send_confirmation?: boolean;
};

export interface AppointmentType {
	id: number;
	name: string;
	description?: string;
	duration_minutes: number;
	color?: string;
	is_active: boolean;
	allow_online_booking: boolean;
	requires_confirmation?: boolean;
	default_price?: string;
	telehealth_enabled: boolean;
	/**
	 * How many of this type ONE patient may hold — `null`/absent means unlimited (S2, ClickUp
	 * 86d3yhdqg). `0` is not representable: the backend validates `nullable|integer|min:1` and
	 * 422s a 0, because 0 reads as "can never be booked" rather than "no cap".
	 *
	 * ⚠️ Only present on the PRACTITIONER payloads (`/practitioner/appointment-types*`). The
	 * column is `$hidden` on the model and re-exposed per request, so the public
	 * `/patient/appointment-types/*` reads deliberately omit it — it is clinic policy.
	 */
	max_bookings_per_patient?: number | null;
	/** Which confirmation email this type sends — `null`/absent means the clinic's default template. */
	confirmation_notification_template_id?: number | null;
	/** Optional so an older backend's payload, which omits it, reads as "no screening". */
	has_screening?: boolean;
	/** Refusal text shown when screening blocks a booking — `null`/absent means the clinic default. */
	screening_block_message?: string | null;
	category?: string;
	custom_fields?: any;
	cliniko_id?: number | null;
	cliniko_data?: any;
	cliniko_synced_at?: string | null;
	created_at?: Date;
	updated_at?: Date;
	practitioners?: TeamMember[];
	workflow_assignments?: WorkflowAssignment[];
	practitioner_workflows?: PractitionerWorkflowAssignment[];
	tandem_rules?: import('./AppointmentSettingsTypes').TandemRule[];
}

/**
 * One patient's usage of ONE capped appointment type, from healthcare's practitioner-only
 * `GET /practitioner/patients/{patientId}/appointment-type-usage` (S10, ClickUp 86d3yhdwc).
 *
 * ⚠️ The list carries CAPPED types only. An uncapped type is simply absent — that absence is
 * the signal to render nothing, rather than "Used 3 of unlimited".
 *
 * ⚠️ `bookings_used` is server-computed and must stay that way: the count normalises the free
 * `status` string ('Cancelled' / 'cancelled' / 'no_show' / 'No show' …) before deciding whether
 * an appointment still holds the allowance. Never recount it from an appointment list here.
 */
export interface AppointmentTypeUsage {
	appointment_type_id: number;
	name: string;
	max_bookings_per_patient: number;
	bookings_used: number;
	/** Never negative — a practitioner may book past the cap, and "-1 remaining" is unrenderable. */
	bookings_remaining: number;
}

/**
 * Which pass produced the stored transcript (S2/S29 of ClickUp 86d3zmpwv).
 *
 * `live` is what the room streamed while the consult was happening; `final` is the offline
 * re-transcription that lands minutes after the save. A practitioner quoting the record needs to
 * know which one they are reading.
 */
export type TranscriptStatus = "live" | "final";

export interface CompletedAppointment {
	id: number;
	app_id: number;
	transcript: Transcript[] | string;
	/**
	 * Absent on every row written before S2 — the completed view renders no badge and polls for
	 * nothing rather than guessing. Needs no line in `convertCompletedAppointmentFromBackend`:
	 * that converter spreads the whole payload and only re-types the two date columns.
	 */
	transcript_status?: TranscriptStatus;
	/**
	 * Speaker label → who the practitioner said that was, as talk stored it on save (S26).
	 *
	 * Typed like `transcript` and for the same reason: healthcare casts the column `json` over a
	 * value talk had already stringified, so the payload carries a JSON **string**, not an object.
	 * `parseSpeakerMap` takes either shape and never throws. Absent on every row written before
	 * S2, and on those the completed view falls back to `Speaker 1` exactly as it always did.
	 * Needs no line in `convertCompletedAppointmentFromBackend`: that converter spreads the whole
	 * payload and only re-types the two date columns.
	 */
	speaker_map?: SpeakerMap | string;
	audio_files: string;
	notes: string;
	report: string;
	vitals: string;
	diagnosis: string;
	suggestions: Suggestion[] | string;
	/** Typed like `transcript`: the `json` cast re-encodes what talk stringified, so it arrives as a (possibly double-encoded) string. */
	chat?: ConsultChatMessage[] | string;
	created_at: Date;
	updated_at: Date;

}

export const defaultConsult: CompletedAppointment = {
	id: 0,
	app_id: 0,
	audio_files: "",
	notes: "",
	report: "",
	transcript: "",
	vitals: "",
	diagnosis: "",
	suggestions: "",
	chat: "",
	created_at: new Date(),
	updated_at: new Date(),
};

export interface AvailablePractitioner {
	// Basic user information
	practitioner_id: UserUuid;
	uuid?: string;
	username?: string;
	first_name: string;
	middle_name?: string;
	last_name: string;
	email?: string;
	phone?: string;
	profile_picture?: string;
	date_of_birth?: string;
	gender?: string;
	address?: string;
	city?: string;
	state?: string;
	country?: string;
	postal_code?: string;
	company?: string;

	// Team member/practitioner specific information
	medical_license_number?: string;
	npi_number?: string;
	dea_number?: string;
	specialties: string[];
	primary_specialty: string;
	years_of_experience?: number;
	qualifications: string[];
	certifications: string[];
	professional_memberships: string[];
	practicing_locations: string[];
	consultation_fee: string;
	biography?: string;
	languages_spoken: string[];
	education_history: string[];
	taxonomy_code?: string;
	supervisor_id?: UserUuid;
	linkage?: string;

	// Availability and scheduling
	availability_schedule: any;
	Available_times: AvailableDay[];

	// Role information
	roles: Role[];

	// Legacy field for backward compatibility
	specialty?: string;
	price?: string;
}

export interface AvailableDay {
	date: string;
	day_name: string;
	slots: TimeSlot[];
	total_blocks?: number;
}

export interface TimeSlot {
	date: string;
	time: string;
	datetime: string;
	display_time: string;
	timezone?: string;
	block_id?: string | null;
}

/**
 * The clinic's PUBLIC booking policy, returned alongside the slots by the UNAUTHENTICATED booking
 * read (healthcare `GET /patient/bookings`, S4).
 *
 * ⚠️ This is NOT `AppointmentPolicy` (below). Same key name (`policy`), different endpoint,
 * different shape — the cancellation policy is deliberately absent from this unauthenticated
 * response, and nothing about cancellation, Cliniko or confirmations belongs on a public route.
 * Keep the two types distinct.
 *
 * `timezone` is the timezone the clinic's OPENING HOURS are defined in — it is not the timezone
 * the slots are displayed in (those stay in the patient's own browser timezone, via each slot's
 * server-preformatted `display_time`). `booking_window_days` is how far ahead the clinic accepts
 * bookings, and bounds the calendar.
 */
export interface BookingPolicy {
	timezone: string;
	booking_window_days: number;
}

export interface AvailableTimesResponse {
	message: string;
	/**
	 * Present on every 200 — both the success path and the "no practitioners" 200 — but ABSENT on
	 * the 404 (appointment type not found or inactive). Optional, and every reader must cope with
	 * it being missing.
	 */
	policy?: BookingPolicy;
	/**
	 * Present on the success 200 only. The controller's "no practitioners available" 200
	 * (`AppointmentController::getAvailableTimes()`) omits it entirely — same shape of absence as
	 * `policy` above, on the same 200. Optional, and every reader must cope with it being missing.
	 */
	appointment_type?: AppointmentType;
	practitioners: AvailablePractitioner[];
}

// Legacy interface for backward compatibility
export interface TeamMemberSchedule {
	id: UserUuid;
	first_name: string;
	last_name: string;
	team_member_info: {
		specialties: string[];
		consultation_fee: string;
	};
}


/**
 * The clinic's patient self-service cancellation policy, returned alongside `data` by the
 * authenticated patient appointment reads (healthcare `GET /patient/{id}/appointments[/upcoming|/past]`).
 *
 * It is a HINT for the UI — the backend re-checks the same policy on cancel and reschedule and
 * remains the only real gate.
 *
 * ⚠️ NOT `BookingPolicy` (above): that one is the public booking read's `policy` block. Same key
 * name, different endpoint, different shape.
 */
export interface AppointmentPolicy {
	allow_cancellation: boolean;
	cancellation_notice_hours: number;
	timezone: string;
}

export const categories = ["Office Visit", "Established Patient", "New Patient", "Health and Behaviour Assessment", "Preventative Care Services", "No Show"];

export const appointmentTypes = ["In Person", "Online"];
export const consultStatuses = [
	"Pending",
	"Reminder done",
	"EMAIL Confirmed",
	"SMS Confirmed",
	"Arrived",
	"Arrived late",
	"Cancelled",
	"Cancelled within 24h",
	"No show",
	"Left w/o visit",
	"In consultation",
	"Chart pulled",
	"Checked out",
	"Transferred Provider",
	"Callback requested",
	"Completed",
	"Post Consult Completed",
];